import React, { useMemo } from "react";
import { Tooltip } from "@mui/material";
import {
  listPictureReceivedDateDetails,
  safeFormatIsoDate,
} from "@/features/propertyCodeAcquisition/utils/pictureReceivedDate";
import { EmptyCellMark } from "@/features/propertyCodeAcquisition/components/EmptyCellMark";
import type { PropertyCodePictureRequestRow } from "@/features/propertyCodeAcquisition/types";

/**
 * 登記取得日（"YYYY-MM-DD"）の表示整形。依頼日列と異なり時刻なし。
 * 不正値・空文字は弾き、例外を投げず「—」を返す（safeFormatIsoDate に集約）。
 */
const formatPictureReceivedDate = (date: string): string =>
  safeFormatIsoDate(date, "yyyy/MM/dd", "—");

/**
 * 「登記取得日」セルの表示。
 * 代表登記取得日（全部事項優先フォールバック）を表示し、Tooltip で種別別の登記取得日明細を示す
 * （1 依頼に複数種別が含まれ登記取得日が異なるケースの取りこぼし防止）。
 * 登記取得日なし行（依頼中・失敗等）は種別セルと同じ「—」表示・Tooltip なし。
 */
const PictureReceivedDateCellComponent: React.FC<{
  row: PropertyCodePictureRequestRow;
  /** DataGrid のセルフォーカス有無（renderCell 引数の hasFocus をそのまま渡す）。 */
  hasFocus?: boolean;
}> = ({ row, hasFocus = false }) => {
  // 列定義側 valueGetter（クリップボード用の代表日）とは用途が異なるため共通化しない。
  const { isEmpty, primaryText, detailText } = useMemo(() => {
    const details = listPictureReceivedDateDetails(row);
    if (details.length === 0) {
      return { isEmpty: true, primaryText: "", detailText: "" };
    }
    return {
      isEmpty: false,
      primaryText: formatPictureReceivedDate(details[0].date),
      detailText: details
        .map((d) => `${d.label}: ${formatPictureReceivedDate(d.date)}`)
        .join(" / "),
    };
  }, [row]);

  if (isEmpty) {
    return <EmptyCellMark />;
  }

  return (
    // describeChild: 代表日付をアクセシブルネームに保ち、明細を説明（aria-describedby）にする。
    // tabIndex: キーボードフォーカスでも Tooltip を開けるようにする。全行を常時タブ対象にすると
    // DataGrid のセル移動とタブ順が衝突するため、フォーカス中セルのみ 0 にする（MUI 推奨パターン）。
    <Tooltip title={detailText} describeChild>
      <span tabIndex={hasFocus ? 0 : -1}>{primaryText}</span>
    </Tooltip>
  );
};

// row は rows 側 useMemo が参照安定を担保するため、他行の再描画時の再計算を防げる。
export const PictureReceivedDateCell = React.memo(
  PictureReceivedDateCellComponent
);
